import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import { authAPI } from '../../services/api';
import MessageModal from '../Common/MessageModal';
import LoginPreferencesBar from './LoginPreferencesBar';

const Container = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: url('/images/background.jpg') center/cover no-repeat fixed;
  padding: 20px;
  position: relative;
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(135deg, rgba(0, 128, 255, 0.82), rgba(1, 5, 84, 0.82));
    z-index: 1;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 520px;
  position: relative;
  z-index: 2;
  background: ${({ theme }) => theme.colors.primary};
  border-radius: 12px;
  padding: 28px;
  box-shadow: 0 16px 40px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.12);
`;

const Title = styled.h1`
  margin: 0 0 10px;
  color: ${({ theme }) => theme.colors.white};
  font-size: 1.6rem;
`;

const Subtitle = styled.p`
  margin: 0 0 18px;
  color: rgba(255, 255, 255, 0.92);
`;

const Input = styled.input`
  width: 100%;
  box-sizing: border-box;
  border: 2px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  padding: 12px;
  font-size: 1rem;
  margin-bottom: 14px;
  background: rgba(255, 255, 255, 0.1);
  color: ${({ theme }) => theme.colors.white};
  &::placeholder {
    color: rgba(255, 255, 255, 0.7);
  }
  &:focus {
    outline: none;
    border-color: rgba(255, 255, 255, 0.35);
  }
`;

const Button = styled.button`
  width: 100%;
  border: 0;
  border-radius: 8px;
  padding: 12px;
  background: ${({ theme }) => theme.colors.buttonPrimary};
  color: ${({ theme }) => theme.colors.white};
  font-weight: 600;
  cursor: pointer;
  opacity: ${(props) => (props.disabled ? 0.65 : 1)};
  &:hover {
    background: ${({ theme }) => theme.colors.buttonPrimaryDark};
  }
`;

const Back = styled(Link)`
  display: inline-block;
  margin-top: 14px;
  color: ${({ theme }) => theme.colors.white};
  text-decoration: none;
  font-weight: 600;
  &:hover {
    text-decoration: underline;
  }
`;

const ForgotPassword = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    title: '',
    message: '',
    type: 'success'
  });

  const openModal = (title, message, type = 'success') => {
    setModal({ open: true, title, message, type });
  };

  const closeModal = () => setModal((prev) => ({ ...prev, open: false }));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    try {
      await authAPI.forgotPassword(email.trim());
      openModal(t('modal.success'), t('auth.forgotSuccess'), 'success');
    } catch (error) {
      if (Number(error?.response?.status) === 404) {
        openModal(
          'Module prêt, branchement email en attente',
          'Le formulaire mot de passe oublié est en place. Le service email transactionnel sera branché à l’étape suivante.',
          'warning'
        );
        return;
      }
      const backendMessage =
        error?.response?.data?.error?.message ||
        'Erreur lors de l’envoi. Réessaie dans quelques instants.';
      openModal(t('modal.error'), backendMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container>
      <Card>
        <LoginPreferencesBar />
        <Title>{t('auth.forgotTitle')}</Title>
        <Subtitle>{t('auth.forgotSubtitle')}</Subtitle>
        <form onSubmit={handleSubmit}>
          <Input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={t('auth.emailPlaceholder')}
            required
            disabled={loading}
          />
          <Button type="submit" disabled={loading}>
            {loading ? t('auth.forgotSubmitting') : t('auth.forgotSubmit')}
          </Button>
        </form>
        <Back to="/">{t('auth.backToLogin')}</Back>
      </Card>

      <MessageModal
        isOpen={modal.open}
        onClose={closeModal}
        title={modal.title}
        message={modal.message}
        type={modal.type}
        variant="auth"
        autoClose={modal.type === 'success'}
        autoCloseDelay={3200}
      />
    </Container>
  );
};

export default ForgotPassword;

